import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSubscription?: boolean;
}

function ProtectedRoute({ children, requireSubscription = false }: ProtectedRouteProps) {
  const { user, loading, subscriptionStatus, subscriptionLoading, isPaywallEnabled } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  // Add a safety timeout to prevent infinite loading
  useEffect(() => {
    if ((loading || (requireSubscription && subscriptionLoading)) && !loadingTimeout) {
      const timer = setTimeout(() => {
        console.warn('Protected route loading timeout triggered');
        setLoadingTimeout(true);
      }, 8000); // 8 second timeout
      
      return () => clearTimeout(timer);
    }
  }, [loading, subscriptionLoading, requireSubscription, loadingTimeout]);

  // We no longer force a check on every route load, which was causing excessive calls
  // The subscription status should already be checked when the user logs in via AuthContext
  // This useEffect has been removed to prevent infinite loops and excessive API calls

  // If loading times out, assume we can proceed (better UX than being stuck)
  if ((loading || (requireSubscription && subscriptionLoading)) && !loadingTimeout) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600 mb-4"></div>
        <p className="text-gray-500 text-sm">Loading your account...</p>
      </div>
    );
  }

  if (!user) {
    // Redirect to login page with the return url
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // Only check subscription if paywall is enabled and this route requires subscription
  // If we hit the loading timeout, bypass this check to prevent stuck states
  if (!loadingTimeout && requireSubscription && isPaywallEnabled && 
      subscriptionStatus !== 'active' && subscriptionStatus !== 'bypass') {
    console.log('Redirecting to subscription page, current status:', subscriptionStatus);
    // Store the current location to return after subscribing
    return <Navigate to="/subscription" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;